import { z } from "zod";

/**
 * Payment terminal hardware providers.
 *
 * This is intentionally separate from `PaymentIntegrationType`: terminals
 * support `adyen`, which is not (yet) a general payment-integration provider,
 * and terminals do not use every integration provider. Defaults to `nakka`
 * since existing terminals use the Nakka credential style.
 */
export const TerminalProviderSchema = z.enum([
  "nakka",
  "payrexx",
  "adyen",
  "stripe",
]);

export type TerminalProvider = z.infer<typeof TerminalProviderSchema>;

/** Create input for a payment terminal (provider-agnostic). */
export const CreateTerminalSchema = z.object({
  name: z.string().min(1),
  provider: TerminalProviderSchema.default("nakka"),
  integration_id: z.string().uuid().nullish(),
  serial_number: z.string().nullish(),
  terminal_id: z.number().int().nullish(),
  location_id: z.string().nullish(),
  password: z.string().optional(),
  client_id: z.string().nullish(),
  client_secret: z.string().nullish(),
  settings: z.record(z.string(), z.unknown()).optional(),
  staff_ids: z.array(z.number()).default([]),
});

export type CreateTerminalInput = z.infer<typeof CreateTerminalSchema>;

/**
 * Update input for a payment terminal (all fields optional / partial).
 *
 * `.partial()` keeps the create defaults, which would turn a name-only update
 * into `provider: "nakka", staff_ids: []` — so drop the defaults here and let
 * omitted fields stay as they are.
 */
export const UpdateTerminalSchema = CreateTerminalSchema.extend({
  provider: TerminalProviderSchema.optional(),
  staff_ids: z.array(z.number()).optional(),
}).partial();

export type UpdateTerminalInput = z.infer<typeof UpdateTerminalSchema>;
